import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import SectionNavPills from '../components/SectionNavPills'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const MyResults = () => {
  const navigate = useNavigate()
  const { user, hasPaidAccess } = useAuth()
  const [attempts, setAttempts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (user) {
      loadAttempts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const loadAttempts = async () => {
    try {
      setLoading(true)
      setError('')

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      const accessToken = sessionData?.session?.access_token
      if (!accessToken) {
        setError('Autentifikácia zlyhala')
        return
      }

      const response = await fetch(`${BACKEND_URL}/api/my-attempts`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať históriu testov')
      }

      const data = await response.json()
      setAttempts(data)
    } catch (err) {
      console.error('Load attempts error:', err)
      setError(err.message || 'Chyba pri načítaní histórie')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getScoreColor = (percentage) => {
    if (percentage >= 75) return 'text-green-700'
    if (percentage >= 50) return 'text-yellow-700'
    return 'text-red-700'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam históriu...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent">
      <SectionNavPills />
      <div className="container mx-auto px-4 py-8 md:py-10">

        {/* Intro card — same EduMisia rhythm as "Moje testy" */}
        <section
          className="bg-gradient-to-br from-white via-blue-50/30 to-emerald-50/20 border border-slate-200 ring-1 ring-slate-100 rounded-2xl shadow-md shadow-slate-200/40 p-6 sm:p-8 mb-6 md:mb-8"
          data-testid="my-results-intro"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Moje výsledky
              </h1>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                Tu vidíte dokončené testy, výsledky a vysvetlenia, ktoré pomáhajú lepšie pochopiť chyby.
              </p>
              <p className="text-sm text-gray-500 mt-2 max-w-2xl">
                Výsledky slúžia ako prehľad prípravy. Ukážu, čo už ide dobre a kde sa oplatí ešte precvičiť.
              </p>
            </div>

            {user && (
              <div className="flex-shrink-0">
                {hasPaidAccess ? (
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 rounded-full font-medium text-sm" data-testid="results-active-badge">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Aktívny prístup
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3.5 py-1.5 bg-slate-50 text-slate-700 ring-1 ring-slate-200 rounded-full font-medium text-sm" data-testid="results-basic-badge">
                    Základný prístup
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Quick navigation back to "Moje testy" */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="group inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-white ring-1 ring-blue-200 hover:bg-blue-50 hover:ring-blue-300 hover:shadow-sm rounded-full shadow-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1"
              data-testid="results-back-to-tests"
            >
              <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Späť na testy
            </button>
            {attempts.length > 0 && (
              <span className="inline-flex items-center px-4 py-2 text-sm text-slate-700 bg-slate-50 ring-1 ring-slate-200 rounded-full">
                Celkovo pokusov: <strong className="ml-1 text-slate-900">{attempts.length}</strong>
              </span>
            )}
          </div>
        </section>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 ring-1 ring-rose-100 rounded-2xl">
            <p className="text-rose-700 text-sm">{error}</p>
          </div>
        )}

        {attempts.length === 0 ? (
          <section
            className="max-w-2xl mx-auto bg-gradient-to-br from-white via-blue-50/30 to-emerald-50/20 border border-slate-200 ring-1 ring-slate-100 rounded-2xl shadow-md shadow-slate-200/40 p-8 sm:p-12 text-center"
            data-testid="my-results-empty"
          >
            <span aria-hidden="true" className="inline-flex w-14 h-14 mb-4 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white items-center justify-center shadow-md shadow-blue-500/25 ring-4 ring-blue-50">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Zatiaľ nemáte žiadne výsledky
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto leading-relaxed">
              Absolvujte svoj prvý test a výsledky sa zobrazia tu.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-b from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-md shadow-blue-500/30 ring-1 ring-blue-700/30 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 transition-all duration-200"
              data-testid="results-empty-cta"
            >
              Prejsť na testy
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </section>
        ) : (
          <div className="space-y-3 sm:space-y-4" data-testid="my-results-list">
            {attempts.map((attempt) => {
              const pct = attempt.percentage || 0
              return (
                <div
                  key={attempt.id}
                  className="bg-white border border-slate-100 ring-1 ring-slate-50 rounded-2xl shadow-sm p-5 sm:p-6 hover:shadow-md hover:ring-blue-100 transition-all duration-200"
                  data-testid={`my-results-item-${attempt.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Left: Test info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                        {attempt.tests?.title || 'Test'}
                      </h3>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(attempt.submitted_at || attempt.started_at)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                          </svg>
                          Pokus č. {attempt.attempt_number}
                        </span>
                        {attempt.tests?.subject && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 ring-1 ring-slate-200 text-xs text-slate-700 capitalize">
                            {attempt.tests.subject}
                          </span>
                        )}
                        {attempt.completed && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            Dokončené
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Middle: Score */}
                    <div className="text-center min-w-[110px]">
                      <div className={`text-2xl sm:text-3xl font-bold ${getScoreColor(pct)}`}>
                        {pct.toFixed(1)}%
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {attempt.score || 0} / {attempt.max_score || 0} bodov
                      </div>
                    </div>

                    {/* Right: Action */}
                    <div className="flex md:flex-col gap-2 md:items-stretch">
                      <button
                        onClick={() => navigate(`/results/${attempt.id}`)}
                        className="flex-1 md:flex-none inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-gradient-to-b from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-sm shadow-blue-500/25 ring-1 ring-blue-700/20 hover:from-blue-700 hover:to-indigo-700 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 transition-all duration-200 whitespace-nowrap"
                        data-testid={`results-view-${attempt.id}`}
                      >
                        Pozrieť výsledok
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyResults
